import React, { useState, useEffect } from 'react';
import { Client } from '../../types/index';
import { ChevronDown, ChevronUp, Search, Undo2, UserX, Users, UserMinus } from 'lucide-react';
import ConfirmationModal from '../common/ConfirmationModal';
import { IconButton } from '../common/IconButton';
import { useClientContext } from '../../contexts/ClientContext';
import { toast } from 'react-toastify';
import { useRole } from '../../hooks/useRole';
import { useClientsAndSubscriptionsQuery, useRestoreClientSubscription } from '../../services/hooks/useClienteSuscripciones';
import { etiquetaSuscripcion, SUSCRIPCION_DOMICILIACION } from '../../utils/suscripciones';
import apiClient from '../../services/api/config';



const DeletedClients: React.FC = () => {
    //estados del contexto
    const { deletedClients, updateDeletedClients, restoreClient } = useClientContext();

    //usuario de clerk
    const { isAdmin } = useRole();

    // Hook para obtener clientes inactivos con JWT
    const { data: inactiveClients = [], isLoading: loading, isError } = useClientsAndSubscriptionsQuery(false);
    
    // Hook para restaurar cliente
    const restoreClientMutation = useRestoreClientSubscription();

    //Estados locales
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [filteredClients, setFilteredClients] = useState<Client[]>([]);
    const [expandedClientId, setExpandedClientId] = useState<string | null>(null);
    const [filter, setFilter] = useState('');
    const [confirmMessage, setConfirmMessage] = useState("");
    const [confirmAction, setConfirmAction] = useState<'delete' | 'restore' | null>(null);
    
    // Sincronizar datos del query con el contexto
    useEffect(() => {
        if (inactiveClients.length > 0) {
            updateDeletedClients(inactiveClients);
        }
    }, [inactiveClients, updateDeletedClients]);

    useEffect(() => {
        const lowercasedFilter = filter.toLowerCase();
        const filteredData = deletedClients.filter(client => {
            const matchesFilter = client.nombre.toLowerCase().includes(lowercasedFilter) ||
                (client.email ?? '').toLowerCase().includes(lowercasedFilter) ||
                client.telefono.includes(lowercasedFilter);
            return matchesFilter;
        });
        setFilteredClients(filteredData);
    }, [deletedClients, filter]);
    
    const toggleExpand = (clientId: string) => {
        setExpandedClientId(expandedClientId === clientId ? null : clientId);
    };

    // La fila entera abre y cierra el detalle. Los botones de acción paran la
    // propagación: si no, restaurar a alguien dejaría además la fila abierta por
    // debajo del modal.
    const filaProps = (clientId: string) => ({
        onClick: () => toggleExpand(clientId),
        role: 'button' as const,
        tabIndex: 0,
        'aria-expanded': expandedClientId === clientId,
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleExpand(clientId);
            }
        },
    });

    // La baja se guarda en la suscripción, no en el cliente, así que para la
    // columna se coge la primera que la tenga.
    const fechaDeBaja = (client: Client): string | null => {
        const conBaja = (client.suscripciones || []).find((suscripcion) => suscripcion.fechaBajaContrato);
        return conBaja?.fechaBajaContrato
            ? new Date(conBaja.fechaBajaContrato).toLocaleDateString()
            : null;
    };

    const handleOpenModalDelete = (client: Client) => {
        setSelectedClient(client);
        setConfirmMessage(`¿Estás seguro de querer eliminar <strong> PERMANENTEMENTE A  ${client.nombre.toLocaleUpperCase()} </strong>?`);
        setConfirmAction('delete');
        setIsModalOpen(true);
    };

    const handleOpenModalRestore = (client: Client) => {
        setSelectedClient(client);
        setConfirmMessage(`¿Estás seguro de querer restaurar a <strong>  ${client.nombre.toLocaleUpperCase()} </strong>?`);
        setConfirmAction('restore');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedClient(null);
    };

    const handleConfirm = () => {
        if (selectedClient) {
            if (confirmAction === 'delete') {
                handleDeletePermanently(selectedClient);
            } else if (confirmAction === 'restore') {
                handleRestore(selectedClient);
            }
            handleCloseModal();
        }
    };

    const handleDeletePermanently = async (client: Client) => {
        const body = {
            clientId: client._id,
            subscriptionId: client.suscripciones && client.suscripciones.length > 0 ? client.suscripciones[0]._id : null
        };
        
        try {
            await apiClient.delete('/client-subscriptions/delete-permanently', {
                data: body
            });

            const updatedClients = deletedClients.filter(c => c._id !== client._id);
            updateDeletedClients(updatedClients);
            setFilteredClients(updatedClients);
            toast.success('Cliente eliminado permanentemente');
        } catch (error) {
            console.error('Error deleting client:', error);
            toast.error('Error eliminando cliente');
        }
    };

    const handleRestore = async (client: Client) => {
        try {
            await restoreClientMutation.mutateAsync({
                clientId: client._id,
                subscriptionId: client.suscripciones && client.suscripciones.length > 0 ? client.suscripciones[0]._id : null
            });

            // Actualiza el estado local
            setFilteredClients(prevClients => prevClients.filter(c => c._id !== client._id));
            
            // Actualiza el estado con el cliente restaurado
            restoreClient(client);
            
        } catch (error) {
            console.error('Error restoring client:', error);
        }
    };

    // Q2 dejó la pantalla con los colores de ahora, pero la estructura seguía
    // siendo tarjetas sueltas sin cabecera. Esto le da la misma forma que "Otros
    // pagos" y que SEPA: panel de totales, buscador y rejilla de 12 columnas.
    //
    // A diferencia de "Otros pagos", aquí la fila es POR CLIENTE y no por
    // suscripción: restaurar y eliminar para siempre son acciones del cliente
    // (`handleRestore` manda `suscripciones[0]`), no de una suscripción suelta.
    return (
        <div className="text-sm bg-white rounded-lg shadow">
            {/* Totales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border-b bg-blue-50">
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <UserMinus className="h-8 w-8 text-red-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Dados de baja</div>
                        <div className="text-xl font-bold">{deletedClients.length}</div>
                    </div>
                </div>
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <Users className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Mostrando</div>
                        <div className="text-xl font-bold">{filteredClients.length}</div>
                    </div>
                </div>
            </div>

            {/* Buscador. No hay filtros avanzados en esta pantalla, así que es solo
                la barra; antes usaba `ClientFilter` con `subscription="DeletedCLients"`,
                que al no ser "SEPA" no pintaba ninguna casilla — era este mismo
                campo con una etiqueta en negrita delante. */}
            <div className="p-4 border-b">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                        type="text"
                        placeholder="Buscar por nombre, email o teléfono..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
            </div>

            {loading && <p className="text-gray-500 p-4">Cargando…</p>}
            {isError && (
                <p className="m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    Error al cargar los clientes dados de baja
                </p>
            )}

            <div className="overflow-x-auto">
                {filteredClients.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No se encontraron clientes.</div>
                ) : (
                    <div className="min-w-full pb-28">
                        {/* Cabecera — solo en escritorio */}
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 p-4 bg-gray-50 border-b font-medium text-sm text-gray-700">
                            <div className="col-span-6">Cliente</div>
                            <div className="col-span-4">Fecha de baja</div>
                            <div className="col-span-2 text-right">Acciones</div>
                        </div>

                        {filteredClients.map((client) => {
                            const expandida = expandedClientId === client._id;
                            const baja = fechaDeBaja(client);

                            const acciones = (
                                <div className="flex items-center justify-end gap-1">
                                    <IconButton
                                        tone="success"
                                        label="Restaurar cliente"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenModalRestore(client);
                                        }}
                                    >
                                        <Undo2 size={18} />
                                    </IconButton>
                                    {isAdmin && (
                                        <IconButton
                                            tone="danger"
                                            label="Eliminar para siempre"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenModalDelete(client);
                                            }}
                                        >
                                            <UserX size={18} />
                                        </IconButton>
                                    )}
                                </div>
                            );

                            const chevron = expandida
                                ? <ChevronUp className="h-4 w-4" />
                                : <ChevronDown className="h-4 w-4" />;

                            return (
                                <div key={client._id} className="border-b">
                                    {/* Fila — escritorio */}
                                    <div
                                        {...filaProps(client._id)}
                                        className="hidden lg:grid lg:grid-cols-12 gap-4 p-4 hover:bg-gray-50 cursor-pointer"
                                    >
                                        {/* min-w-0 en los dos niveles: sin él un hijo de flex no baja
                                            de su ancho intrínseco y un correo largo se sale por encima
                                            de la siguiente columna. */}
                                        <div className="col-span-6 min-w-0">
                                            <div className="flex items-center min-w-0">
                                                <span className="text-blue-500 p-1 mr-2 shrink-0">{chevron}</span>
                                                <div className="min-w-0">
                                                    <div className="font-medium truncate" title={client.nombre}>{client.nombre}</div>
                                                    {client.email && (
                                                        <div className="text-sm text-gray-500 truncate select-all" title={client.email}>{client.email}</div>
                                                    )}
                                                    {client.telefono && (
                                                        <div className="text-sm text-gray-500 truncate select-all">{client.telefono}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-span-4">
                                            {baja
                                                ? <span className="px-2 py-0.5 text-xs rounded bg-red-50 text-red-700">{baja}</span>
                                                : <span className="text-gray-400">—</span>}
                                        </div>

                                        <div className="col-span-2">{acciones}</div>
                                    </div>

                                    {/* Fila — móvil y tablet */}
                                    <div {...filaProps(client._id)} className="lg:hidden p-4 cursor-pointer">
                                        <div className="flex items-start gap-2">
                                            <span className="text-blue-500 p-1 shrink-0">{chevron}</span>
                                            {/* D4 — `break-all` en el correo: una dirección larga no tiene
                                                dónde partirse y estiraba la tarjeta fuera de la pantalla. */}
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium truncate">{client.nombre}</div>
                                                <div className="text-sm text-gray-500 break-all">{client.email}</div>
                                                <div className="text-sm text-gray-500">{client.telefono}</div>
                                                {baja && (
                                                    <div className="mt-1">
                                                        <span className="px-2 py-0.5 text-xs rounded bg-red-50 text-red-700">Baja: {baja}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="shrink-0">{acciones}</div>
                                        </div>
                                    </div>

                                    {/* Detalle */}
                                    {expandida && (
                                        <div className="bg-gray-50 p-4 border-t">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                    <h3 className="font-semibold text-gray-800 pb-2 text-sm border-b border-gray-200 mb-2">Datos del cliente</h3>
                                                    <p className="p-1 text-xs text-gray-700"><strong>DNI:</strong> {client.dni}</p>
                                                    <p className="p-1 text-xs text-gray-700"><strong>Fecha de Alta:</strong> {new Date(client.fechaAlta).toLocaleDateString()}</p>
                                                    <p className="p-1 text-xs text-gray-700"><strong>Creado por:</strong> {client.createdBy}</p>
                                                    <p className="p-1 text-xs text-gray-700"><strong>Observaciones:</strong> {client.observaciones}</p>
                                                </div>

                                                {/* Antes esto solo se pintaba si alguna suscripción era
                                                    `pilates-domiciliacion`. A un cliente de otros pagos dado
                                                    de baja le salía "No hay suscripciones.", que es falso:
                                                    la tiene, solo que de otro tipo. Ahora pasa cualquiera y
                                                    el IBAN y el pagador salen únicamente en domiciliación,
                                                    que es donde significan algo. */}
                                                {(client.suscripciones || []).length === 0 ? (
                                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                                                        No hay suscripciones.
                                                    </div>
                                                ) : (
                                                    client.suscripciones.map((suscripcion) => {
                                                        const esDomiciliacion = suscripcion.nombreSuscripcion === SUSCRIPCION_DOMICILIACION;
                                                        return (
                                                            <div key={suscripcion._id} className="bg-white border border-gray-200 rounded-lg p-4">
                                                                <div className="font-semibold text-gray-800 pb-2 text-sm border-b border-gray-200 mb-2">
                                                                    Datos de {etiquetaSuscripcion(suscripcion.nombreSuscripcion).toLowerCase()}
                                                                </div>
                                                                {esDomiciliacion && (
                                                                    <p className="p-1 text-xs text-gray-700">
                                                                        <strong>IBAN:</strong> {suscripcion.iban ? suscripcion.iban.replace(/(.{4})/g, '$1 ') : 'NO TENÍA CUENTA REGISTRADA'}
                                                                    </p>
                                                                )}
                                                                {suscripcion.fechaBajaContrato && (
                                                                    <p className="px-1.5 py-0.5 text-xs rounded bg-red-50 text-red-700 inline-block my-1">
                                                                        <strong>Fecha de la Baja:</strong> {new Date(suscripcion.fechaBajaContrato).toLocaleDateString()}
                                                                    </p>
                                                                )}
                                                                {suscripcion.tipoClase && <p className="p-1 text-xs text-gray-700"><strong>Nivel de la Clase:</strong> {suscripcion.tipoClase}</p>}
                                                                <p className="p-1 text-xs text-gray-700"><strong>Frecuencia:</strong> {suscripcion.frecuencia}</p>
                                                                <p className="p-1 text-xs text-gray-700"><strong>Importe:</strong> {suscripcion.importeSuscripcion} €</p>
                                                                <p className="p-1 text-xs text-gray-700"><strong>Descuento:</strong> {suscripcion.descuento} €</p>
                                                                <p className="p-1 text-xs text-gray-700"><strong>Fecha Alta Contrato:</strong> {new Date(suscripcion.fechaAltaContrato).toLocaleDateString()}</p>
                                                                {esDomiciliacion && suscripcion.pagador && (
                                                                    <div className="mt-4 ml-4">
                                                                        <h2 className="mb-2"><strong className="text-sm text-gray-800">Pagador</strong></h2>
                                                                        <p className="p-1 text-xs text-gray-700"><strong>Nombre:</strong> {suscripcion.pagador.nombre}</p>
                                                                        <p className={!suscripcion.pagador.IBAN ? 'px-1.5 py-0.5 text-xs rounded bg-red-50 text-red-700 inline-block my-1' : 'p-1 text-xs text-gray-700'}>
                                                                            <strong>IBAN:</strong> {suscripcion.pagador.IBAN ? suscripcion.pagador.IBAN.replace(/(.{4})/g, '$1 ') : 'FALTA INFORMACIÓN'}
                                                                        </p>
                                                                        <p className="p-1 text-xs text-gray-700"><strong>DNI:</strong> {suscripcion.pagador.DNI}</p>
                                                                        <p className="p-1 text-xs text-gray-700"><strong>Teléfono:</strong> {suscripcion.pagador.telefono}</p>
                                                                        <p className="p-1 text-xs text-gray-700"><strong>Email:</strong> {suscripcion.pagador.email}</p>
                                                                    </div>
                                                                )}
                                                                <p className="p-1 text-xs text-gray-700"><strong>Observaciones:</strong> {suscripcion.observaciones && suscripcion.observaciones}</p>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Fuera de la lista: estaba dentro del `length > 0`, así que si el
                listado se quedaba vacío por debajo, el modal abierto desaparecía. */}
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirm}
                confirmMessage={confirmMessage}
            />
        </div>
    );
};

export default DeletedClients;
